import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';

import { environment } from './environments/environment';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { sanitizeErrorEvent } from '@packages/shared-utils';

/**
 * Validates that a Sentry DSN is a real, non-placeholder value.
 * Real DSNs follow the format: https://publicKey@host/projectId
 *
 * @param dsn - The DSN string to validate
 * @returns true if the DSN is a valid Sentry DSN, false for undefined/null or placeholder values
 */
function isValidSentryDsn(dsn: string | undefined): dsn is string {
  if (!dsn) {
    return false;
  }
  // Check that DSN starts with https:// and contains @ (real DSN format)
  return dsn.startsWith('https://') && dsn.includes('@');
}

async function main(): Promise<void> {
  let telemetryModuleLoadError: unknown = null;
  try {
    const { initTelemetry } = await import('./telemetry');
    initTelemetry(environment);
  } catch (err) {
    telemetryModuleLoadError = err;
    console.error('[telemetry] browser OpenTelemetry module failed to load', err);
  }

  // Initialize Sentry if DSN is configured with a valid, non-placeholder value.
  if (isValidSentryDsn(environment.sentryDsn)) {
    Sentry.init({
      dsn: environment.sentryDsn,
      environment: environment.production ? 'production' : 'development',
      release: `brokers-portal@${environment.version}`,
      beforeSend: sanitizeErrorEvent,
      integrations: [],
      tracePropagationTargets: [],
    });

    if (telemetryModuleLoadError) {
      Sentry.captureException(telemetryModuleLoadError);
    }
  }

  await bootstrapApplication(AppComponent, appConfig);
}

main().catch((err) => console.error(err));
